import { useSuspenseQuery, UseSuspenseQueryResult } from '@tanstack/react-query';
import { paths } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';

type SEATermsResponse = paths['/user/terms']['get']['responses']['200']['content']['application/json'];
export type SEATerms = SEATermsResponse['data'];

interface UseShowTermsBannerResult {
  latestTerms: SEATerms[number] | undefined;
  showTermsBanner: boolean;
}

export const QUERY_KEY_USER_TERMS = 'user-terms';

const fetchTerms = async (): Promise<SEATerms> => {
  const termsResponse = await apiClient.get('api/supplier-experience/user/terms').json<SEATermsResponse>();
  return termsResponse.data ?? [];
};

const useTerms = <TData = SEATerms>(select?: (data: SEATerms) => TData): UseSuspenseQueryResult<TData> => {
  return useSuspenseQuery<SEATerms, Error, TData>({
    queryKey: [QUERY_KEY_USER_TERMS],
    queryFn: fetchTerms,
    select,
  });
};

export const useShowTermsBanner = (): UseShowTermsBannerResult => {
  const {
    data: { latestTerms, showTermsBanner },
  } = useTerms((terms: SEATerms) => {
    const latestTerms = terms.find((term) => term?.kind === 'TERMS_AND_CONDITIONS' && !term.hasSeen);
    const showTermsBanner = !!latestTerms;
    return { latestTerms, showTermsBanner };
  });

  return { latestTerms, showTermsBanner };
};

interface UseShowPrivacyPolicyBannerResult {
  latestPrivacyPolicy: SEATerms[number] | undefined;
  showPrivacyPolicyBanner: boolean;
}

export const useShowPrivacyPolicyBanner = (): UseShowPrivacyPolicyBannerResult => {
  const {
    data: { latestPrivacyPolicy, showPrivacyPolicyBanner },
  } = useTerms((terms: SEATerms) => {
    const latestPrivacyPolicy = terms.find((term) => term?.kind === 'PRIVACY_POLICY' && !term.hasAcknowledged);
    const showPrivacyPolicyBanner = !!latestPrivacyPolicy;
    return { latestPrivacyPolicy, showPrivacyPolicyBanner };
  });

  return { latestPrivacyPolicy, showPrivacyPolicyBanner };
};

export default useTerms;
